import PropTypes from 'prop-types';
import React, {useMemo, useState, useEffect} from 'react';
import {useDebouncedEffect} from '@ozgene/gp5.hooks.use-debounced-effect';
import {getStringChangeHandler} from '@ozgene/gp5.utils.data-functions';
import {TextField, Grid, Switch, FormControlLabel} from '@material-ui/core';
import {validateUniqueEmail, validateUniqueUsername} from 'UserLobby/service/UserService';
import SelectImportedProject from 'ImportedProjectLobby/components/SelectImportedProject';

export default function UserEditor(props) {
  const {entity, onPropertyChange, onValidityChange} = props;
  const {
    id = '',
    username = '',
    firstName = '',
    lastName = '',
    email = '',
    password,
    admin,
    suppressNotifications = false,
    importedProjects = [],
  } = entity;
  const [usernameErrorText, setUsernameErrorText] = useState('');
  const [emailErrorText, setEmailErrorText] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const isUsernameInvalid = useMemo(
    () => !username || !!usernameErrorText,
    [username, usernameErrorText],
  );
  const isEmailInvalid = useMemo(() => !email || !!emailErrorText, [email, emailErrorText]);
  const handleChangeUsername = getStringChangeHandler('username', onPropertyChange);
  const handleChangeFirstName = getStringChangeHandler('firstName', onPropertyChange);
  const handleChangeLastName = getStringChangeHandler('lastName', onPropertyChange);
  const handleChangeEmail = getStringChangeHandler('email', onPropertyChange);
  const handleChangeAdmin = () => onPropertyChange({admin: !admin});
  const handleChangeSuppressNotifications = () =>
    onPropertyChange({suppressNotifications: !suppressNotifications});
  const handleChangeImportedProjects = (projects) => onPropertyChange({importedProjects: projects});

  useDebouncedEffect(
    () => {
      const validateUsername = async () => {
        setUsernameErrorText(await validateUniqueUsername(username, id));
        setIsValidating(false);
      };

      return validateUsername();
    },
    [username],
    500,
  );
  useDebouncedEffect(
    () => {
      const validateEmail = async () => {
        setEmailErrorText(await validateUniqueEmail(email, id));
        setIsValidating(false);
      };

      return validateEmail();
    },
    [email],
    500,
  );
  useEffect(() => {
    setIsValidating(true);
  }, [username, email, password]);
  useEffect(
    () =>
      onValidityChange(
        !isValidating && !isUsernameInvalid && !isEmailInvalid && firstName && lastName,
      ),
    [isValidating, isUsernameInvalid, isEmailInvalid, onValidityChange, firstName, lastName],
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={6} sm={4}>
        <TextField
          fullWidth
          label={'First Name'}
          value={firstName}
          inputProps={{maxLength: 100}}
          onChange={handleChangeFirstName}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField
          fullWidth
          label={'Last Name'}
          value={lastName}
          inputProps={{maxLength: 100}}
          onChange={handleChangeLastName}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField
          fullWidth
          label={'Username'}
          value={username}
          inputProps={{maxLength: 100}}
          onChange={handleChangeUsername}
          helperText={usernameErrorText}
          error={isUsernameInvalid}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField
          fullWidth
          label={'Email'}
          value={email}
          inputProps={{maxLength: 100}}
          onChange={handleChangeEmail}
          helperText={emailErrorText}
          error={isEmailInvalid}
        />
      </Grid>
      <Grid item xs={6} sm={4}>
        <TextField fullWidth label={'Password'} value={password} disabled />
      </Grid>
      <Grid item xs={6} sm={4}>
        <SelectImportedProject
          label="Imported Projects"
          value={importedProjects}
          onChange={handleChangeImportedProjects}
          multiple
        />
      </Grid>
      <Grid item xs={6}>
        <FormControlLabel
          control={<Switch onChange={handleChangeAdmin} style={{width: 'auto'}} checked={admin} />}
          label="Admin"
        />
      </Grid>
      <Grid item xs={6}>
        <FormControlLabel
          control={
            <Switch
              onChange={handleChangeSuppressNotifications}
              style={{width: 'auto'}}
              checked={suppressNotifications}
            />
          }
          label="Suppress Notifications"
        />
      </Grid>
    </Grid>
  );
}

UserEditor.propTypes = {
  entity: PropTypes.shape({
    id: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    username: PropTypes.string,
    password: PropTypes.string,
    email: PropTypes.string,
    admin: PropTypes.bool,
    suppressNotifications: PropTypes.bool,
    importedProjects: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onValidityChange: PropTypes.func.isRequired,
};
